
import { LoaderComponent } from 'vue-loading-overlay';
import { Component, Emit, InjectReactive, ProvideReactive, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, Mutation } from 'vuex-class';

import CategorySelector from '@/components/CategorySelector.vue';
import ContinueShoppingModal from '@/components/ContinueShoppingModal.vue';
import OptionModifierGroup, { getModifierInfo } from '@/components/OptionModifierGroup.vue';
import ProductActions from '@/components/ProductActions.vue';
import ProductDetails from '@/components/ProductDetails.vue';
import { MetaKeys, MutationTypes, Views } from '@/enums';
import BasketModule from '@/store/modules/basket';
import { prepareOptionModifiersChoices } from '@/utils/products.util';

const { SET_ACTIVE_CATEGORY, SET_ACTIVE_PRODUCT } = MutationTypes;

@Component({
  components: {
    CategorySelector,
    OptionModifierGroup,
    ProductActions,
    ProductDetails,
  },
  metaInfo: {
    title: 'Cousins Subs',
    titleTemplate: '%s - Order - Choose Food'
  }
})
export default class ProductView extends Vue {
  @InjectReactive('progressRef') progressRef: any;
  @InjectReactive() isMobile!: boolean;
  @ProvideReactive('requestInProgress') requestInProgress = false;
  @Getter editIsOpen: boolean;
  @Mutation(SET_ACTIVE_CATEGORY) setActiveCategory: (category: Category | null) => void;

  // [x: string]: any;
  @Getter activeRestaurant!: Restaurant;
  @Getter activeProduct: Product;
  @Getter activeProductModifiers: ProductModifier[];
  @Getter categories: Category[];
  @Action fetchProductModifiers: ({ productId, restaurantId }: { productId: string; restaurantId: number }) => void;
  @Action fetchRestaurantBySlug: ({ slug }: { slug: string }) => Restaurant;
  @Action fetchRestaurant: ({ restaurantId }: { restaurantId: number }) => Restaurant;
  @Action fetchRestaurantMenu: ({ slug }: { slug: string }) => void;
  @Mutation(SET_ACTIVE_PRODUCT) setActiveProduct: (product: Product) => void;

  bottomHeight: string;
  quantity: number;
  specialInstructions: string;
  showOptionModifiers: boolean;
  selectedPrimaryModifier: any;
  selectedSecondaryModifier: any;
  selectedProductModifierOption: any;
  setUserSelections: any;
  tempUserSelections: any;
  isValid: boolean;
  stickyBottom: boolean;

  getModifierInfo = getModifierInfo;

  data() {
    return {
      bottomHeight: 'auto',
      quantity: 1,
      specialInstructions: '',
      showOptionModifiers: false,
      selectedPrimaryModifier: {},
      selectedSecondaryModifier: {},
      selectedProductModifierOption: {},
      setUserSelections: {},
      tempUserSelections: { },
      isValid: false,
      stickyBottom: true,
    }
  }

  get isNavigationSelectable() {
    const hasNavSelectableMeta = this.activeProductModifiers?.[0]?.metaData?.find(({ key }) => key.toLowerCase() === MetaKeys.navigationSelectable.toLowerCase());
    return hasNavSelectableMeta;
  }

  get shallowItem() {
    const isShallow = this.activeProductModifiers?.every((pm) => pm.productModifierOptions?.every((pmo) => !pmo.optionModifiers?.length));
    return isShallow;
  }

  get isDeepProduct() {
    return !this.shallowItem && this.isNavigationSelectable;
  }

  getCategoryFromSlug(): Category {
    const { categorySlug } = this.$route.params;
    return this.categories.find((category) => category.slug === categorySlug) as Category || {} as Category;
  }

  async created() {
    let loader: LoaderComponent | undefined;
    try {
      const { location, categorySlug } = this.$route.params;
      let restaurant: Restaurant = this.activeRestaurant;

      if (location && (restaurant?.slug !== location || (BasketModule.current.id && BasketModule.data.vendorId !== restaurant.id))) {
        if (!BasketModule.current?.id) {
          loader = this.$loading.show();
          restaurant = await this.fetchRestaurantBySlug({ slug: location });
          loader.hide();
        } else {
          loader = this.$loading.show();
          restaurant = await this.fetchRestaurant({ restaurantId: BasketModule.data.vendorId });
          loader.hide();
        }

        if (restaurant.slug !== location) {
          this.$router.push({
            name: this.$route.name as string,
            params: {
              ...this.$route.params,
              location: restaurant.slug,
            }
          });
        }
      }

      if (restaurant?.slug) {
        loader = this.$loading.show();
        await this.fetchRestaurantMenu({ slug: restaurant.slug });
        loader.hide();
      }

      if (categorySlug) {
        const category = this.getCategoryFromSlug();
        this.setActiveCategory(category);
      }

      const product = this.getProductFromSlug();
      if (product.id) {
        this.setActiveProduct(product);
        loader = this.$loading.show();
        await this.fetchProductModifiers({ productId: product.id.toString(), restaurantId: restaurant.id });
        loader.hide();

        if (this.editingProduct) {
          if (this.shallowItem) {
            this.activeProductModifiers.forEach((productModifier) => {
              const pmos = productModifier?.productModifierOptions?.filter((o) => {
                const found = this.editingProduct?.choices.find((c) => o.id === c.optionId);
                return o.id && found;
              });

              if (pmos?.length) {
                const { isSingleOption } = this.getModifierInfo(productModifier);
                let usePmos: ProductModifierOption[] | ProductModifierOption | number[] = pmos;
                if (isSingleOption && (usePmos as ProductModifierOption[])?.length === 1) {
                  this.selectedPrimaryModifier = this.selectedPrimaryModifier?.id ? this.selectedPrimaryModifier : productModifier;
                  usePmos = usePmos?.[0];
                  this.handleProductModifierOption(usePmos, productModifier, true);
                } else {
                  usePmos = usePmos.map((pmo) => pmo.id);
                  this.handleOption(productModifier, usePmos, this.shallowItem);
                }
              }
            });
          } else {
            // match product.choices to results from this.fetchProductModifiers to determine existing selections
            this.activeProductModifiers.forEach((productModifier) => {
              const pmo = productModifier?.productModifierOptions?.find((pmo) => {
                return this.editingProduct?.choices.find((c) => {
                  return c.optionId === pmo.id;
                });
              });
              if (pmo) {
                this.handleProductModifierOption(pmo, productModifier, true);
                if (pmo?.optionModifiers?.length) {
                  this.setSelection(false);
                }
              }
            });
          }
          this.quantity = this.editingProduct?.quantity || this.minQuantity;
          this.specialInstructions = this.editingProduct?.specialInstructions || '';
        } else {
          this.quantity = this.minQuantity;
          // load navigation selectable item ie subs
          const productModifier = this.activeProductModifiers?.[0] || null;
          const pmo = productModifier?.productModifierOptions?.[0] || null;
          if (this.shallowItem || (product.productModifiers?.length === 1 && product.productModifiers[0].productModifierOptions?.length === 1)) {
            const { isSingleOption } = this.getModifierInfo(productModifier);
            let changes: number | number[] = 0;
            const defaultMods: ProductModifierOption[] = productModifier?.productModifierOptions?.filter((o) => o.isDefault);
            if (isSingleOption && defaultMods.length <= 1) {
              changes = defaultMods[0]?.id || productModifier?.productModifierOptions[0]?.id || changes;
            } else {
              changes = defaultMods?.map((mod) => mod.id);
            }
            this.selectedPrimaryModifier = productModifier;
            this.handleOption(productModifier, changes, this.shallowItem);
          } else if (this.isDeepProduct) {
            if (productModifier && pmo) {
              this.handleProductModifierOption(pmo, productModifier, true);
              this.setSelection(false);
            }
          } else {
            this.selectedPrimaryModifier = productModifier;
          }
        }
      } else {
        this.$router.push({
          name: 'ChooseFood',
          params: {
            ...this.$route.params
          }
        });
      }
    } catch {
    } finally {
      if (loader) {
        loader.hide();
      }
    }
  }

  mounted() {
    Vue.nextTick(() => {
      const targetLabels = ['meal deal', 'party box', 'box lunch']; // Add other combo keywords if needed

      function trySelect(): boolean {
        const labels = document.querySelectorAll('label');

        for (const label of labels) {
          const text = label.textContent?.toLowerCase().trim() || '';

          if (targetLabels.some(keyword => text.includes(keyword))) {
            const forAttr = label.getAttribute('for');

            // Safe casting to HTMLInputElement
            const input = forAttr
              ? document.getElementById(forAttr) as HTMLInputElement
              : label.querySelector('input[type="radio"]') as HTMLInputElement;

            if (input && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
              console.log(`✅ Selected combo via label match: "${text}"`);
              return true;
            }
          }
        }

        console.log('⚠️ No matching label found.');
        return false;
      }

      let attempts = 0;
      const interval = setInterval(() => {
        const success = trySelect();
        attempts++;
        if (success || attempts >= 20) clearInterval(interval);
      }, 500);
    });
  }

  updated() {
    const bottom: any = this.$refs.bottom;
    this.bottomHeight = bottom ? `${bottom?.clientHeight}px` : 'auto';
  }

  getProductFromSlug(): Product {
    const { categorySlug, productSlug } = this.$route.params;
    const currentCategory = this.categories.find((category) => category.slug === categorySlug);
    return currentCategory?.products.reduce((acc, product) => {
      if (product.slug === productSlug) {
        acc = product;
      }
      return acc;
    }, {} as Product) || {} as Product;
  }

  handleSelectPrimaryModifierOption(pm: ProductModifier, pmo: number) {
    const option = pm?.productModifierOptions?.find((option: ProductModifierOption) => option.id === pmo) || {} as ProductModifierOption;
    if (!option?.id && !pm?.id) {
      return this.$log.error('value missing in handleSelectPrimaryModifierOption');
    }

    const skipSet = this.handleProductModifierOption(option, pm);
    if (!skipSet) {
      this.setSelection(false);
    }
  }

  handleProductModifierOption(
    pmo: ProductModifierOption,
    pm: ProductModifier,
    onLoad = false,
    reload = !!this.editingProduct,
  ) {
    if (!pmo?.optionModifiers?.length && pmo?.id) {
      this.$set(this.setUserSelections, pm.id, { [pmo.id]: pmo.id });
      return true;
    } else if (pmo?.optionModifiers?.length) {
      this.tempUserSelections = (pmo.optionModifiers || []).reduce((acc: any, om) => {
        // create array for multiple choice optionModifiers and 0: number for single
        const { isSingleOption } = this.getModifierInfo(om);
        acc[om.id] = acc[om.id] || (isSingleOption ? 0 : []);

        // IF temp object has been started and we go back to the same pmo, set those properties
        // ELSE if they do not match, set the defaults
        // also consider if we are editing a current product

        const editedProductModifier = this.setUserSelections[pm.id];
        const editedProductPmoId = Object.entries(editedProductModifier || {})?.find(([key, val]: any) => parseInt(key, 10) === val) || 0;
        if (editedProductModifier && parseInt(editedProductPmoId[0]) === pmo.id) {
          const selectedEntities = Object.entries(this.setUserSelections || {})?.map(([key, omSelections]: any) => {
            if (typeof omSelections !== 'number' && parseInt(key) === pm.id) {
              const omEntities: [string, any][] = Object.entries(omSelections)
              return omEntities;
            }
          }).filter(Boolean);

          const selectedOptionModifiers: [string, any][] = selectedEntities[0]?.find(([key]: any) => parseInt(key) === om.id) || [];
          if (selectedOptionModifiers[1]) {
            acc[om.id] = selectedOptionModifiers[1];
          }
        } else if (reload && onLoad) {
          // set editing selections
          this.setOnId(acc, om);
        } else {
          // set defaults
          this.setDefault(acc, om);
        }

        return acc;
      }, {
        // add selectedProductModifierOption id by default
        [pmo.id]: pmo.id,
      });

      if (!onLoad && this.isMobile) {
        this.$scrollTo(this.$refs.productSelectionsContainer as Element, { force: true });
      }

      this.selectedProductModifierOption = { ...pmo, parentId: pm.id };
      this.selectedPrimaryModifier = pm;
      this.handleSelectSecondaryModifier(pmo?.optionModifiers?.[0] || {});
      this.showOptionModifiers = !onLoad;
      return false;
    }
  }

  // TODO~> need to pass matcher method
  setOnId(acc, om) {
    om.optionModifierChoices.forEach((choice) => {
      const matchChoice = this.editingProduct?.choices?.find((c) => c?.optionId === choice?.id);
      if (matchChoice) {
        if (Array.isArray(acc[om.id])) {
          acc[om.id].push(choice.id);
        } else {
          acc[om.id] = choice.id;
        }
      }
    })
  }

  setOnName(acc, om) {
    // TODO~> this is a case of switching sub sizes, need to get the name of the current selections and find the new mactching choice by name
    om.optionModifierChoices.forEach((choice) => {
      const matchChoice = this.editingProduct?.choices?.find((c) => {
        // regex should be agnostic towards calories but not cost i.e. matches breads but not extras
        // that could maybe be done in the cost comparison ??
        const reg = /(^[a-zA-Z0-9_ \\+$\\.]+)[\\(\\+0-9a-z\\)]?/;
        const [, ecName] = reg.exec(c?.name) || [];
        const [, choiceName] = reg.exec(choice?.name) || [];
        return ecName === choiceName && c?.cost === choice?.cost;
      });
      if (matchChoice) {
        if (Array.isArray(acc[om.id])) {
          acc[om.id].push(choice.id);
        } else {
          acc[om.id] = choice.id;
        }
      }
    })
  }

  setDefault(acc, om) {
    let hasDefault = false;
    om.optionModifierChoices.forEach((choice) => {
      if (choice.isDefault) {
        hasDefault = true;
        if (Array.isArray(acc[om.id])) {
          acc[om.id].push(choice.id);
        } else {
          acc[om.id] = choice.id;
        }
      }
    });

    // If no default, select first option
    if (!hasDefault && om.isMandatory) {
      const firstChoice = om?.optionModifierChoices[0];
      if (firstChoice) {
        if (Array.isArray(acc[om.id])) {
          acc[om.id].push(firstChoice.id);
        } else {
          acc[om.id] = firstChoice.id;
        }
      }
    }
  }

  get editingProduct() {
    const { basketProductId } = this.$route.params;
    return BasketModule.current?.products?.find((product) => product?.id === parseInt(basketProductId, 10));
  }

  handleAddToItem() {
    this.setSelection();
    this.$scrollTo(this.$refs.productSelectionsContainer as Element);
    this.showOptionModifiers = false;
  }

  setSelection(clearTempSelections = true) {
    this.$set(this.setUserSelections, this.selectedProductModifierOption.parentId || this.selectedPrimaryModifier.id, this.tempUserSelections);
    if (clearTempSelections) {
      this.tempUserSelections = {};
    }
  }

  async handleAddItem() {
    if (!BasketModule.current?.id) {
      this.$emit('showEdit');
    } else {
      this.requestInProgress = true;
      // prepare data for basket
      const product = this.getProductFromSlug();
      if (product) {
        const choices = prepareOptionModifiersChoices(this.setUserSelections);
        //  populate body
        const productRequest = {
          choices,
          productId: product.id,
          quantity: this.quantity,
          specialInstructions: this.specialInstructions
        };

        try {
          // send data to action
          if (this.editingProduct) {
            await BasketModule.updateProduct({
              ...productRequest,
              id: this.editingProduct.id,
            });
          } else {
            await BasketModule.addItem({
              ...productRequest,
              basketId: BasketModule.id,
            });
          }

          if (!this.editingProduct) {
            this.showContinueShopping({ success: `Your ${this.activeProduct.name} was successfully added!` });
          } else {
            this.$router.push(
              {
                name: 'OrderSummary',
                params: {
                  ...this.$route.params
                }
              });
          }
        } catch (err) {
          // TODO~> pop error dialogue with err.data: string; instead of toast
          this.$toasted.error(err.data);
          this.$log.error(err);
        } finally {
          this.requestInProgress = false;
        }
      }
    }
  }

  handleNavigation({ params }) {
    const { location } = this.$route.params;
    if (params === 'continue') {
      this.setActiveCategory(null);

      this.$router.push({
        name: Views.CHOOSE_FOOD,
        params: {
          ...this.$route.params,
          location,
        }
      });
    } else if (params === 'checkout') {
      this.$router.push({
        ...this.$route.params,
        name: Views.ORDER_SUMMARY,
      });
    }
  }

  showContinueShopping(params) {
    this.$modal.show(
      ContinueShoppingModal, {
        isMobile: this.isMobile,
        message: params.success,
      }, {
        clickToClose: false,
        scrollable: true,
        focusTrap: true,
        height: 'auto',
        width: this.isMobile ? '90%' : '60%',
      }, {
        'before-close': this.handleNavigation
      }
    );
  }

  findChoices(pmo) {
    return pmo.optionModifiers?.optionModifierChoices?.length
      ? pmo.optionModifiers?.optionModifierChoices
      : pmo.optionModifiers?.length
        ? pmo.optionModifiers
        : pmo;
  }

  hasOptionModifiers(pmo) {
    return !!pmo?.optionModifiers?.length;
  }

  hasProductModifierOptions(pmo) {
    return !!pmo?.productModifierOptions?.length;
  }

  get imagePath() {
    return this.activeProduct?.desktopImageUrl;
  }

  get selectedOptionModifiers() {
    return this.selectedProductModifierOption?.optionModifiers || [];
  }

  get selectedModifier() {
    return this.selectedProductModifierOption?.id
      ? this.selectedProductModifierOption
      : this.useProductModifiers[0]?.productModifierOptions?.[0]
        ? this.useProductModifiers[0]?.productModifierOptions?.[0]
        : {};
  }

  get cost() {
    return this.productMeta.cost?.toFixed(2);
  }

  get baseCalories() {
    return this.productMeta.baseCalories;
  }

  get maxCalories() {
    return this.productMeta.maxCalories;
  }

  get useProductModifiers() {
    return this.activeProductModifiers?.length
      ? this.activeProductModifiers
      : this.activeProduct?.productModifiers?.length
        ? this.activeProduct.productModifiers
        : [];
  }

  get useOptionModifier() {
    return this.selectedPrimaryModifier?.id
      ? this.selectedPrimaryModifier
      : [];
  }

  @Watch('tempUserSelections', { immediate: false, deep: true })
  handleSecondaryChange() {
    if (this.isNavigationSelectable || !this.isMobile) {
      this.setSelection(false);
    }
  }

  handleSelectPrimaryModifier(modifier) {
    if (this.isDeepProduct) {
      this.selectedSecondaryModifier = modifier;
    } else {
      const [pmoId] = Object.entries(this.setUserSelections || {})?.map(([, omSelections]: any) => {
        if (typeof omSelections !== 'number') {
          const omEntities = Object.entries(omSelections)
          // find pmo id
          const [, pmoId]: any = omEntities?.find(([key, val]: any) => parseInt(key, 10) === val) || [];
          return pmoId;
        }
      }).filter(Boolean);

      const productModiferOption = modifier?.productModifierOptions?.find((opt) => opt.id === pmoId);
      if (pmoId && productModiferOption && !this.shallowItem) {
        // find associated pmo
        this.selectedProductModifierOption = productModiferOption;
        this.handleProductModifierOption(productModiferOption, modifier, false, false);
      } else {
        this.selectedPrimaryModifier = modifier;
        this.selectedSecondaryModifier = {};
      }
    }
  }

  handleSelectSecondaryModifier(modifierOption) {
    if (this.selectedSecondaryModifier?.id === modifierOption?.id) {
      this.selectedSecondaryModifier = {};
      return;
    }
    this.selectedSecondaryModifier = modifierOption;
  }

  get primaryModifiers() {
    return this.isDeepProduct ? this.selectedOptionModifiers : this.activeProductModifiers;
  }

  get selectedPrimaryModifierOptions() {
    return this.selectedPrimaryModifier?.productModifierOptions?.length
      ? this.selectedPrimaryModifier.productModifierOptions
      : this.selectedPrimaryModifier?.optionModifierChoices?.length
        ? this.selectedPrimaryModifier.optionModifierChoices
        : [];
  }

  get secondaryModifiers() {
    return this.isDeepProduct ? [] : this.selectedProductModifierOption?.optionModifiers || [];
  }

  get secondaryModifierOptions() {
    return this.selectedSecondaryModifier?.optionModifierChoices?.length
      ? this.selectedSecondaryModifier.optionModifierChoices
      : [];
  }

  get primaryTargetKey() {
    return this.selectedPrimaryModifier.id;
  }

  get primaryTargetValue() {
    const { isSingleOption } = this.getModifierInfo(this.selectedPrimaryModifier);
    let value = Object.entries(this.setUserSelections[this.primaryTargetKey] || {}).reduce((acc: any, [key, val]) => {
      const parsedKey = parseInt(key, 10);
      if (parsedKey === val) {
        acc = val;
      } else if (parsedKey === this.primaryTargetKey) {
        acc = val;
      }
      return acc;
    }, 0);

    if (!value && !isSingleOption) {
      value = [];
    }
    return value;
  }

  get validations() {
    return this.activeProductModifiers.reduce((acc: any[], apm: ProductModifier) => {
      const userProMods = this.setUserSelections[apm.id];
      if (!userProMods) {
        acc.push(new Error(`Please Choose a ${apm.description}`));
      }
      return acc;
    }, []);
  }

  get productMeta(): any {
    try {
      const useSelections = this.setUserSelections;
      // product modifiers and their product modifier options
      return Object.entries(useSelections).reduce((acc, [, omSelections]: any) => {
        const omEntities = Object.entries(omSelections);
        const arrayOfPmos = omEntities?.[0]?.[1];
        // find pmo id
        const [, pmoId]: any = omEntities.find(([key, val]: any) => parseInt(key, 10) === val) || [];
        // isSingleOption radio options ProductModifierOptions
        if (pmoId && omEntities?.length === 1) {
          const productModifierOption = this.findProductModifierOptionById(pmoId);
          return this.setMetaValues(acc, productModifierOption);
        } else if (!pmoId && Array.isArray(arrayOfPmos)) {
          // multiple selection product modifier options
          arrayOfPmos.forEach((pmoId) => {
            const productModifierOption = this.findProductModifierOptionById(pmoId);
            this.setMetaValues(acc, productModifierOption);
          })
          return acc;
        } else {
          this.processProductModifierMeta(acc, omEntities, pmoId);
          return acc;
        }
      }, {
        cost: this.activeProduct.cost,
        baseCalories: parseInt(this.activeProduct.baseCalories),
        maxCalories: parseInt(this.activeProduct.maxCalories),
      });
    } catch (err) {
      this.$log.error(err);
    }
  }

  setMetaValues(acc, option: ProductModifierOption | OptionModifierChoice) {
    if (option?.id) {
    // if (option?.adjustsParentCalories) {
      const baseCalories = parseInt(option.baseCalories as string, 10);
      acc.baseCalories = acc.baseCalories + baseCalories;

      const maxCalories = parseInt(option.maxCalories as string, 10);
      acc.maxCalories = acc.maxCalories + maxCalories;
    }
    if (option?.id) {
    // if (option?.adjustsParentPrice) {
      acc.cost = acc.cost + option.cost;
    }
    return acc;
  }

  processProductModifierMeta(acc, omEntities, pmoId) {
    omEntities.forEach(([omId, omcSelect]: any) => {
      const productModifierOption = this.findProductModifierOptionById(pmoId);
      if (!productModifierOption) {
        return;
      }
      // sub size
      if (productModifierOption.id === omcSelect) {
        this.setMetaValues(acc, productModifierOption);
      } else {
        // option modifier choices
        // check if its a radio or multi checkbox
        const isArray = Array.isArray(omcSelect);
        const useSelect = isArray ? omcSelect : [omcSelect];
        const optionModifier = productModifierOption?.optionModifiers?.find((om: OptionModifier) => om.id === parseInt(omId, 10));
        // get all optionModifierChoices
        const cs = optionModifier?.optionModifierChoices?.filter((omc) => useSelect.includes(omc.id));
        cs.forEach((c) => {
          this.setMetaValues(acc, c);
        })
      }
    });

    return acc;
  }

  get summaryDetails(): any {
    try {
      // NOTE: the const's below allow for just the active primary modifier details to be shown
      // const hasTemp = Object.keys(this.tempUserSelections || {})?.length;
      // const userSelections = { temp: { ...this.tempUserSelections, [this.selectedProductModifierOption?.id]: this.selectedProductModifierOption?.id } };
      // const useSelections = hasTemp ? userSelections : this.setUserSelections;
      const useSelections = this.setUserSelections;
      // product modifiers and their product modifier options
      return Object.entries(useSelections).map(([, omSelections]: any) => {
        const omEntities = Object.entries(omSelections);
        const arrayOfPmos = omEntities?.[0]?.[1];
        // find pmo id
        const [, pmoId]: any = omEntities.find(([key, val]: any) => parseInt(key, 10) === val) || [];
        // isSingleOption radio options ProductModifierOptions
        if (pmoId && omEntities?.length === 1) {
          const acc: any = { primary: [], secondary: [] };
          const productModifierOption = this.findProductModifierOptionById(pmoId);
          acc.primary.unshift(productModifierOption?.name);
          return acc;
        } else if (!pmoId && Array.isArray(arrayOfPmos)) {
          // multiple selection product modifier options
          const acc: any = { primary: [], secondary: [] };
          arrayOfPmos.forEach((pmoId) => {
            const productModifierOption = this.findProductModifierOptionById(pmoId);
            acc.primary.unshift(productModifierOption?.name);
          })
          return acc;
        } else {
          // option modifiers and their option modifier choices
          const productModifierOption = this.findProductModifierOptionById(pmoId);
          if (!productModifierOption) {
            return;
          }
          return this.createProductModifierSummary(omEntities, productModifierOption);
        }
      }).filter(Boolean);
    } catch (err) {
      this.$log.error(err);
      return { primary: [], secondary: [] };
    }
  }

  orderSummaryByOptionModifiers(omEntities, productModifierOption) {
    for (const [omId] of [...omEntities]) {
      const omIndex = productModifierOption?.optionModifiers?.findIndex((om: OptionModifier) => om.id === parseInt(omId, 10));
      const index = omEntities?.findIndex(([key]: any) => parseInt(key, 10) === parseInt(omId, 10));
      if ((index >= 0) && (omIndex >= 0) && (index !== omIndex)) {
        const removed = omEntities.splice(index, 1);
        omEntities.splice(omIndex, 0, ...removed);
      }
    }
    return omEntities;
  }

  createProductModifierSummary(omEntities, productModifierOption) {
    omEntities = this.orderSummaryByOptionModifiers(omEntities, productModifierOption);
    return omEntities.reduce((acc: any, [omId, omcSelect]: any) => {
      // sub size
      if (productModifierOption.id === omcSelect) {
        acc.primary.unshift(productModifierOption.name);
      } else {
        // option modifier choices
        // check if its a radio or multi checkbox
        const isArray = Array.isArray(omcSelect);
        const useSelect = isArray ? omcSelect : [omcSelect];
        const optionModifier = productModifierOption?.optionModifiers?.find((om: OptionModifier) => om.id === parseInt(omId, 10));
        // get all optionModifierChoices
        const cs = optionModifier?.optionModifierChoices?.filter((omc) => useSelect.includes(omc.id));
        const names = (cs || []).map((c: OptionModifierChoice) => c.name);
        if (optionModifier?.description === 'Bread:') {
          acc.primary.push(...names);
        } else {
          acc.secondary.push(...names);
        }
      }

      return acc;
    }, { primary: [], secondary: [] })
  }

  joinPrimarySummary(summ) {
    if (!summ?.secondary?.length) {
      return summ?.primary?.join(' & ') || '';
    }
    return summ?.primary?.join(' on ') || '';
  }

  joinSecondarySummary(summ) {
    return summ?.secondary?.join(', ') || '';
  }

  findProductModifierOptionById(pmoId) {
    return this.activeProductModifiers.reduce((iAcc: any, pm: ProductModifier) => {
      const productModifier = pm.productModifierOptions.find((pmo) => pmo.id === pmoId);
      if (productModifier) { iAcc = productModifier; } return iAcc;
    }, null);
  }

  @Watch('setUserSelections', { immediate: false, deep: true })
  validate() {
    this.isValid = this.validations.length === 0;
  }

  productImagePath(product: Product) {
    const fileName = product?.desktopImageUrl;
    if (!fileName) {
      return null;
    }
    return `${fileName}`;
  }

  handleIntersect(event) {
    const bool = event?.find((e) => e.target?.id === 'page-bottom')?.isIntersecting;
    if (bool !== undefined) {
      this.stickyBottom = !bool;
    }
  }

  get caloriesDisplay() {
    const omcId = this.tempUserSelections[this.selectedSecondaryModifier.id];
    if (omcId) {
      const optionModifierChoice = this.selectedSecondaryModifier.optionModifierChoices.find((omc) => omc.id === omcId)
      if (optionModifierChoice) {
        const metaData = optionModifierChoice.metaData?.find(({ key }) => key.toLowerCase() === 'basecalories');
        if (metaData) {
          return metaData.value;
        }
      }
    }

    if (this.baseCalories && this.baseCalories > 0) {
      return this.maxCalories && this.maxCalories > 0 ? `${this.baseCalories}-${this.maxCalories}` : this.baseCalories;
    }
    return null;
  }

  handleOption(modifier, change, shallow) {
    const { isSingleOption } = this.getModifierInfo(modifier);
    if (isSingleOption) {
      this.handleSelectPrimaryModifierOption(modifier, change);
    } else if (shallow) {
      this.$set(this.setUserSelections, modifier?.id, { [modifier?.id]: change });
    } else {
      this.$set(this.tempUserSelections, modifier?.id, change);
    }
  }

  getPmValue(proMod) {
    return this.setUserSelections[proMod.id]?.[proMod.id];
  }

  getPmoValue(proMod, pmo) {
    return this.setUserSelections[proMod.id]?.[pmo.id];
  }

  get hasNoOptionModifiers() {
    return this.useProductModifiers.every((proMod) => proMod.productModifierOptions.every((pmo) => !this.hasOptionModifiers(pmo)));
  }

  get minQuantity() {
    return 1; // Olo ordering leaves min Quantity at 1 and throws error on check out, this is inline with that functionality
  }

  handleQuantityChange(quant) {
    if (quant < this.minQuantity) {
      this.quantity = this.minQuantity;
    } else {
      this.quantity = quant;
    }
  }

  @Emit('handleChooseCategory')
  handleChooseCategory(category) {
    this.$scrollTo(this.$refs.product as Element, { force: true });
    return { category, callback: undefined };
  }
}
